import React, {useEffect, useState} from 'react'
import {ButtonGroup, Button, DateTimeRangePicker, Icon} from '@therms/atalaya'
import {EventsHistograms} from '@src/modules/Stats/components/EventsHistograms'
import {
  ISODateString,
  LabeledSelectLocation,
  useCurrentUser,
  useLocationContext,
  UserAnPCheck,
  useUrlState,
} from '@therms/react-modules'
import dayjs from 'dayjs'
import {LocationLogEntriesHistograms} from '@src/modules/Stats/components/EntriesHistograms'
import {ReportsHistograms} from '@src/modules/Stats/components/ReportsHistograms'
import {MdDateRange, MdEditCalendar} from 'react-icons/md'
import classNames from 'classnames'

function Stats() {
  const locationContext = useLocationContext()
  const user = useCurrentUser()
  const [timeframeBtn, setTimeframeBtn] = useState('7d')
  const [timeframe, setTimeframe] = useUrlState<{
    end?: ISODateString
    start?: ISODateString
  }>('stats_timeframe', {
    end: dayjs().endOf('day').toISOString(),
    start: dayjs().subtract(7, 'day').startOf('day').toISOString(),
  })
  const [locationIds, setLocationIds] = useUrlState<string[]>('statsLocIds', [])

  const cleanedTimeframe = React.useMemo(
    () => ({
      end: timeframe.end ? dayjs(timeframe.end).endOf('day').toISOString() : '',
      start: timeframe.start
        ? dayjs(timeframe.start).startOf('day').toISOString()
        : '',
    }),
    [timeframe],
  )

  // Determine which locations the stats should be scoped to. When viewing a
  // single location (location context) or when the user has explicitly picked
  // locations, use those. Otherwise default to the Portal user's assigned
  // locations so the dashboard reflects only what they can access — regardless
  // of region. Users with access to all locations fall back to the server's
  // region-level scoping (undefined filter).
  const locationAndGroupIds = React.useMemo<string[] | undefined>(() => {
    if (locationContext?.locationId) return [locationContext.locationId]

    if (locationIds.length > 0) return locationIds

    return user.anp.locations.all
      ? undefined
      : user.anp.locations.locationAndGroupIds
  }, [locationContext?.locationId, locationIds, user.anp.locations])

  const displayTimeframe = React.useMemo(() => {
    const start = timeframe.start
      ? dayjs(timeframe.start).format('MMM D, YYYY')
      : 'No Start Date'
    const end = timeframe.end
      ? dayjs(timeframe.end).format('MMM D, YYYY')
      : 'No End Date'

    return `${start} - ${end}`
  }, [timeframeBtn, timeframe])

  useEffect(() => {
    if (timeframe.start && !timeframe.end) {
      setTimeframe((prevState) => ({
        end: dayjs().endOf('day').toISOString(),
        start: prevState.start,
      }))
    }
  }, [timeframe])

  return (
    <div className="p-sm">
      <div
        className={classNames(
          {'justify-between': !locationContext, 'justify-end': locationContext},
          'z-sticky sticky top-0 flex w-full',
        )}
      >
        {!locationContext && (
          <div className="z-sticky sticky top-0 flex max-w-96 grow justify-start">
            <div className="max-w-96 grow">
              <LabeledSelectLocation
                value={locationIds}
                multi
                locationAndGroupIds={
                  user.anp.locations.all
                    ? undefined
                    : user.anp.locations.locationAndGroupIds
                }
                onComplete={setLocationIds}
              />
            </div>
          </div>
        )}

        <div className="z-sticky sticky top-0 flex justify-end">
          <div className="bg-surface flex rounded">
            <div className="bg-surface-subtle rounded-l">
              {timeframeBtn === 'manual' ? (
                <DateTimeRangePicker
                  mode="date"
                  onChangeValue={setTimeframe}
                  value={timeframe}
                  showClearValueButton={false}
                />
              ) : (
                <div className="p-xxs text-color-strong flex items-center font-medium">
                  <span className="px-xxs">{displayTimeframe}</span>

                  <Button
                    onClick={() => {
                      setTimeframeBtn('manual')
                    }}
                    subtle
                    variant={timeframeBtn === 'manual' ? 'main' : undefined}
                    size="sm"
                  >
                    <Icon Icon={MdEditCalendar} />
                  </Button>
                </div>
              )}
            </div>

            <ButtonGroup size="sm" subtle>
              <Button
                onClick={() => {
                  setTimeframeBtn('7d')
                  setTimeframe({
                    end: dayjs().endOf('day').toISOString(),
                    start: dayjs().subtract(7, 'days').toISOString(),
                  })
                }}
                variant={timeframeBtn === '7d' ? 'main' : undefined}
              >
                7 days
              </Button>

              <Button
                onClick={() => {
                  setTimeframeBtn('30d')
                  setTimeframe({
                    end: dayjs().endOf('day').toISOString(),
                    start: dayjs().subtract(30, 'days').toISOString(),
                  })
                }}
                variant={timeframeBtn === '30d' ? 'main' : undefined}
              >
                30 days
              </Button>

              <Button
                onClick={() => {
                  setTimeframeBtn('90d')
                  setTimeframe({
                    end: dayjs().endOf('day').toISOString(),
                    start: dayjs().subtract(90, 'days').toISOString(),
                  })
                }}
                variant={timeframeBtn === '90d' ? 'main' : undefined}
              >
                90 days
              </Button>
            </ButtonGroup>
          </div>
        </div>
      </div>

      {!timeframe.start && !timeframe.end ? (
        <div className="mt-xxl mx-auto max-w-(--breakpoint-sm) text-center">
          <Icon Icon={MdDateRange} size="xxl" color="subtle" />
          <div className="mt-xs text-md text-color-subtle font-medium">
            No Timeframe selected <br /> Please select a start and end date from
            the controls above.
          </div>
        </div>
      ) : (
        <>
          <UserAnPCheck
            check={(user) => user.anp.role.portal.access.viewEvents}
          >
            <h2 className="mb-base bg-background pb-xxs text-color-strong text-lg font-bold tracking-wide">
              Events
            </h2>

            <EventsHistograms
              endDate={cleanedTimeframe.end}
              locationAndGroupIds={locationAndGroupIds}
              startDate={cleanedTimeframe.start || ''}
            />
          </UserAnPCheck>

          <UserAnPCheck
            check={(user) => user.anp.role.portal.access.viewLocationLogs}
          >
            <h2 className="mb-base bg-background pb-xxs text-color-strong text-lg font-bold tracking-wide">
              Log Entries
            </h2>

            <LocationLogEntriesHistograms
              endDate={cleanedTimeframe.end}
              locationAndGroupIds={locationAndGroupIds}
              startDate={cleanedTimeframe.start || ''}
            />
          </UserAnPCheck>

          <UserAnPCheck
            check={(user) => user.anp.role.portal.access.viewReports}
          >
            <h2 className="mb-base bg-background pb-xxs text-color-strong text-lg font-bold tracking-wide">
              Reports
            </h2>

            <ReportsHistograms
              endDate={cleanedTimeframe.end}
              refs={locationAndGroupIds}
              startDate={cleanedTimeframe.start || ''}
            />
          </UserAnPCheck>
        </>
      )}
    </div>
  )
}

export {Stats}
